import { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight, Leaf, Sparkles, Check, Loader2, Send } from "lucide-react";
import { SunriseCircle } from "../components/icons/SunriseCircle";
import {
  BRAND,
  IMAGES,
  PILLARS,
  TESTIMONIALS,
  FAMILIAR,
  HOME_OFFERS,
  HOME_FAQS,
} from "../data/content";
import { MaskedLines, FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialBooking = { name: "", email: "", phone: "", package_interest: "", message: "" };

export default function Home() {
  const heroRef = useRef(null);
  const lenis = useLenis();
  const location = useLocation();
  const scrollTo = (target) => {
    if (lenis) lenis.scrollTo(target, { offset: -80, duration: 1.0 });
    else document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => scrollTo(location.hash), 450);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.75]);

  // discovery-call booking form
  const [form, setForm] = useState(initialBooking);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSent(true);
      setForm(initialBooking);
      toast.success("Your message is on its way. Noelle will be in touch soon.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // waitlist (The Gathering)
  const [wlOpen, setWlOpen] = useState(false);
  const [wlForm, setWlForm] = useState({ name: "", email: "", phone: "" });
  const [wlLoading, setWlLoading] = useState(false);
  const [wlDone, setWlDone] = useState(false);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!wlForm.name.trim() || !wlForm.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setWlLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { ...wlForm, cohort: "The Gathering — priority list" });
      setWlDone(true);
      toast.success("You're on the priority list. We'll be in touch about the next Gathering.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setWlLoading(false);
    }
  };

  const handleOffer = (offer) => {
    if (offer.waitlist) {
      setWlDone(false);
      setWlOpen(true);
    }
  };

  return (
    <div data-testid="home-page">
      {/* 1 — HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[680px] overflow-hidden" data-testid="hero">
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          <img src={IMAGES.hero} alt="Two women at a sunlit coastline" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div className="absolute inset-0 bg-brand-forest" style={{ opacity: overlayOpacity }} />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-forest/80 via-brand-forest/25 to-brand-forest/40" />

        <motion.div
          className="relative z-10 h-full flex flex-col justify-center max-w-7xl mx-auto px-6 md:px-10"
          style={{ y: textY }}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="flex items-center gap-3 mb-8"
          >
            <SunriseCircle className="text-brand-goldBright" size={22} strokeWidth={1.4} />
            <span className="overline text-brand-goldBright">{BRAND.tagline}</span>
          </motion.div>

          <h1 className="font-serif text-brand-cream text-[2.15rem] leading-[1.08] sm:text-5xl md:text-6xl lg:text-7xl md:leading-[1.03] tracking-tight max-w-4xl">
            <MaskedLines lines={["You've spent years", "showing up for", "everyone else."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
            className="font-serif italic text-brand-goldBright text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.1] mt-4 max-w-3xl"
          >
            When did you stop showing up for you?
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-xl text-brand-cream/90 text-lg md:text-xl leading-relaxed font-light"
          >
            Brighter Tomorrows helps high-functioning women — the ones who carry everything for
            everyone else — reconnect with themselves, rediscover what they truly want, and
            architect a life that genuinely reflects who they are.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-12 flex flex-col sm:flex-row flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#discovery-call")}
              className="group inline-flex items-center justify-center gap-3 bg-brand-goldBright text-brand-forest px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:-translate-y-1 hover:shadow-2xl transition-transform duration-300"
            >
              I'm Ready for Something to Change
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#familiar")}
              className="inline-flex items-center justify-center gap-3 border border-brand-cream/40 text-brand-cream px-8 py-4 rounded-full text-sm tracking-wide hover:bg-brand-cream/10 transition-colors duration-300"
            >
              Explore Brighter Tomorrows
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* 2 — DOES THIS FEEL FAMILIAR? */}
      <section id="familiar" className="bg-brand-cream py-28 md:py-40" data-testid="familiar">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">Does this feel familiar?</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-forest tracking-tight mt-5 max-w-3xl leading-[1.05]">
              If any of these quietly live inside you, you're exactly where you're meant to begin.
            </h2>
          </FadeUp>

          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16" stagger={0.08}>
            {FAMILIAR.map((item, i) => (
              <StaggerItem key={i}>
                <div
                  data-testid={`familiar-${i}`}
                  className="group h-full flex items-start gap-4 bg-brand-beige/50 border border-brand-gold/15 rounded-3xl p-8 hover:bg-brand-forest transition-colors duration-500"
                >
                  <SunriseCircle className="text-brand-gold shrink-0 mt-1 group-hover:text-brand-goldBright transition-colors" size={22} strokeWidth={1.4} />
                  <p className="font-serif text-xl md:text-2xl italic text-brand-forest group-hover:text-brand-cream transition-colors duration-500 leading-snug">
                    {item}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <FadeUp delay={0.15}>
            <div className="mt-14">
              <button
                data-testid="familiar-cta"
                onClick={() => scrollTo("#perspective")}
                className="group inline-flex items-center gap-2 font-serif italic text-2xl md:text-3xl text-brand-forest link-underline hover:text-brand-gold transition-colors duration-300"
              >
                This is me <ArrowRight size={22} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* 3 — THE PERSPECTIVE SHIFT */}
      <section id="perspective" className="bg-brand-forest text-brand-cream py-28 md:py-44 relative grain overflow-hidden" data-testid="perspective">
        <div className="max-w-4xl mx-auto px-6 md:px-10 relative z-10 text-center">
          <FadeUp><span className="overline text-brand-goldBright">The perspective shift</span></FadeUp>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-brand-goldBright tracking-tight mt-8 leading-[1.05]">
            <MaskedLines lines={["You don't need to try harder.", "You need a life that includes you."]} />
          </h2>
          <FadeUp delay={0.2}>
            <p className="text-brand-cream/85 text-lg md:text-xl leading-relaxed mt-10 max-w-2xl mx-auto font-light">
              So many women have become extraordinary at surviving — achieving, caregiving, meeting
              every expectation — while slowly disconnecting from themselves. Brighter Tomorrows is
              the bridge between simply surviving and intentionally designing what comes next.
            </p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <button
              data-testid="perspective-cta"
              onClick={() => scrollTo("#method-home")}
              className="group inline-flex items-center gap-3 mt-12 bg-brand-cream text-brand-forest px-10 py-4 rounded-full text-sm tracking-wide hover:bg-brand-goldBright hover:-translate-y-1 transition-[background-color,transform] duration-300"
            >
              Discover What's Possible
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </FadeUp>
        </div>
      </section>

      {/* 4 — THE METHOD */}
      <section id="method-home" className="bg-brand-beige py-28 md:py-40" data-testid="method-home">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 lg:gap-16">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <FadeUp><span className="overline">The Brighter Tomorrows Method™</span></FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-forest tracking-tight mt-5 leading-[1.05]">
                  We don't just talk about the life you want. We architect it.
                </h2>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p className="text-brand-inkSoft text-lg leading-relaxed mt-8 max-w-md">
                  Six pillars that turn insight into a life you can actually live — built from
                  identity, not willpower.
                </p>
              </FadeUp>
              <FadeUp delay={0.3}>
                <Link
                  to="/method"
                  data-testid="method-home-cta"
                  className="group inline-flex items-center gap-2 mt-10 font-serif italic text-2xl text-brand-forest link-underline hover:text-brand-gold transition-colors duration-300"
                >
                  Explore the Six Pillars <ArrowRight size={22} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </FadeUp>
            </div>
          </div>

          <div className="md:col-span-7">
            <Stagger className="grid sm:grid-cols-2 gap-6" stagger={0.08}>
              {PILLARS.map((p, i) => (
                <StaggerItem key={p.title}>
                  <div
                    data-testid={`home-pillar-${i}`}
                    className="group h-full bg-brand-cream hover:bg-brand-forest rounded-3xl p-8 transition-colors duration-500"
                  >
                    <div className="flex items-center justify-between mb-6">
                      {i % 3 === 0 ? <Leaf className="text-brand-gold group-hover:text-brand-goldBright transition-colors" strokeWidth={1.2} size={28} /> : i % 3 === 1 ? <SunriseCircle className="text-brand-gold group-hover:text-brand-goldBright transition-colors" strokeWidth={1.4} size={28} /> : <Sparkles className="text-brand-gold group-hover:text-brand-goldBright transition-colors" strokeWidth={1.2} size={28} />}
                      <span className="font-serif text-brand-sage group-hover:text-brand-cream/40 text-2xl transition-colors">0{i + 1}</span>
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl text-brand-forest group-hover:text-brand-cream tracking-tight transition-colors duration-500">{p.title}</h3>
                    <p className="text-brand-inkSoft group-hover:text-brand-cream/75 leading-relaxed mt-3 text-sm transition-colors duration-500">{p.body}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      <Marquee items={["Clarity", "Confidence", "Grace", "Becoming"]} />

      {/* 5 — SOCIAL PROOF */}
      <section className="bg-brand-cream py-28 md:py-40 grain" data-testid="social-proof">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline">Real women. Real becoming.</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-forest tracking-tight mt-5 max-w-3xl leading-[1.03]">
              Women like you are already becoming.
            </h2>
          </FadeUp>

          <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16" stagger={0.08}>
            {TESTIMONIALS.slice(0, 4).map((t, i) => (
              <StaggerItem key={i}>
                <div data-testid={`home-testimonial-${i}`} className="h-full bg-brand-beige/40 border border-brand-gold/15 rounded-3xl p-8">
                  <SunriseCircle className="text-brand-gold mb-6" size={24} strokeWidth={1.3} />
                  <h3 className="font-serif italic text-2xl text-brand-forest leading-snug">{t.title}</h3>
                  <p className="text-brand-inkSoft leading-relaxed mt-4 text-sm">{t.body}</p>
                  <p className="overline text-brand-gold mt-6 text-[0.6rem]">— {t.author}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* 6 — WAYS TO WORK */}
      <section id="offers" className="bg-brand-forest text-brand-cream py-28 md:py-40 scroll-mt-24" data-testid="offers">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline text-brand-goldBright">Ways to work with Brighter Tomorrows</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.03]">
              Choose how you want to begin.
            </h2>
          </FadeUp>

          <Stagger className="grid md:grid-cols-3 gap-6 mt-16 items-stretch" stagger={0.1}>
            {HOME_OFFERS.map((o) => (
              <StaggerItem key={o.id} className="h-full">
                <div
                  data-testid={`offer-card-${o.id}`}
                  className={`relative h-full flex flex-col rounded-[2rem] p-8 lg:p-10 transition-transform duration-500 hover:-translate-y-2 ${
                    o.featured
                      ? "bg-brand-cream text-brand-forest border-2 border-brand-goldBright shadow-2xl md:scale-[1.04]"
                      : "bg-brand-forestSoft/40 border border-brand-cream/10 text-brand-cream"
                  }`}
                >
                  {o.featured && (
                    <span className="absolute top-7 right-7 overline text-brand-gold">Most Chosen</span>
                  )}
                  <SunriseCircle className={o.featured ? "text-brand-gold" : "text-brand-goldBright"} size={30} strokeWidth={1.4} />
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tight mt-6">{o.name}</h3>
                  <p className={`mt-2 text-sm ${o.featured ? "text-brand-inkSoft" : "text-brand-cream/70"}`}>{o.tag}</p>
                  <p className={`text-sm ${o.featured ? "text-brand-inkSoft" : "text-brand-cream/70"}`}>{o.cadence}</p>

                  <div className="mt-6 flex items-end gap-2">
                    <span className="font-serif text-5xl tracking-tight" data-testid={`offer-price-${o.id}`}>{o.price}</span>
                    <span className={`mb-1.5 text-xs ${o.featured ? "text-brand-inkSoft" : "text-brand-cream/60"}`}>investment</span>
                  </div>

                  <p className={`mt-6 leading-relaxed flex-1 ${o.featured ? "text-brand-inkSoft" : "text-brand-cream/80"}`}>{o.desc}</p>

                  {o.note && (
                    <p className={`mt-5 text-sm italic ${o.featured ? "text-brand-gold" : "text-brand-goldBright"}`}>{o.note}</p>
                  )}

                  {o.waitlist ? (
                    <button
                      data-testid={`offer-cta-${o.id}`}
                      onClick={() => handleOffer(o)}
                      className={`group mt-8 inline-flex items-center justify-center gap-3 w-full px-8 py-4 rounded-full text-sm tracking-wide font-medium transition-transform duration-300 hover:-translate-y-0.5 ${
                        o.featured ? "bg-brand-forest text-brand-cream" : "bg-brand-goldBright text-brand-forest"
                      }`}
                    >
                      {o.cta} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  ) : (
                    <Link
                      to="/packages"
                      data-testid={`offer-cta-${o.id}`}
                      className={`group mt-8 inline-flex items-center justify-center gap-3 w-full px-8 py-4 rounded-full text-sm tracking-wide font-medium transition-transform duration-300 hover:-translate-y-0.5 ${
                        o.featured ? "bg-brand-forest text-brand-cream" : "bg-brand-cream text-brand-forest"
                      }`}
                    >
                      {o.cta} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  )}
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <FadeUp delay={0.15}>
            <p className="text-center text-brand-cream/70 mt-12 text-sm">
              Not sure which is right for you?{" "}
              <button data-testid="offers-to-discovery" onClick={() => scrollTo("#discovery-call")} className="text-brand-goldBright link-underline">
                Start with a discovery call →
              </button>
            </p>
          </FadeUp>
        </div>
      </section>

      {/* 7 — DISCOVERY CALL */}
      <section id="discovery-call" className="bg-brand-beige py-28 md:py-40 scroll-mt-24" data-testid="discovery-call">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-5">
            <FadeUp><span className="overline">Your complimentary discovery call</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-brand-forest tracking-tight leading-[1.03] mt-5">
                You don't have to know exactly what you need.
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="text-brand-inkSoft text-lg leading-relaxed mt-8 max-w-md">
                Let's talk about where you are, what feels out of alignment, and what you want your
                next chapter to look like. Together, we'll determine whether Brighter Tomorrows is
                the right next step for you.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-10 space-y-4">
                {["No pressure, no obligation", "A warm, honest conversation", "Clarity on your best next step"].map((line) => (
                  <div key={line} className="flex items-center gap-3 text-brand-forest">
                    <SunriseCircle className="text-brand-gold shrink-0" size={18} strokeWidth={1.5} />
                    <span className="text-brand-inkSoft">{line}</span>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>

          <div className="md:col-span-7">
            <FadeUp delay={0.2}>
              <div className="bg-brand-cream rounded-[2rem] p-8 md:p-12 shadow-[0_20px_60px_-30px_rgba(26,51,43,0.35)]">
                {sent ? (
                  <div className="text-center py-16" data-testid="discovery-success">
                    <div className="w-16 h-16 rounded-full bg-brand-forest mx-auto flex items-center justify-center">
                      <Check className="text-brand-goldBright" size={30} />
                    </div>
                    <h3 className="font-serif text-4xl text-brand-forest mt-8">Thank you.</h3>
                    <p className="text-brand-inkSoft mt-4 max-w-sm mx-auto">
                      Your request has been received. Noelle will reach out personally to schedule your call.
                    </p>
                    <button data-testid="discovery-reset" onClick={() => setSent(false)} className="mt-8 text-brand-forest link-underline text-sm">
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submitBooking} className="space-y-7" data-testid="discovery-call-form">
                    <div className="flex items-center gap-3 mb-1">
                      <SunriseCircle className="text-brand-gold" size={24} strokeWidth={1.4} />
                      <span className="overline">Begin the conversation</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="d-name" className="text-brand-forest text-sm">Your name *</Label>
                        <Input id="d-name" data-testid="discovery-input-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg" />
                      </div>
                      <div>
                        <Label htmlFor="d-email" className="text-brand-forest text-sm">Email *</Label>
                        <Input id="d-email" type="email" data-testid="discovery-input-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="d-phone" className="text-brand-forest text-sm">Phone (optional)</Label>
                      <Input id="d-phone" data-testid="discovery-input-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1 (555) 000-0000" className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg" />
                    </div>
                    <div>
                      <Label htmlFor="d-message" className="text-brand-forest text-sm">What's the story in your head? *</Label>
                      <Textarea id="d-message" data-testid="discovery-input-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} placeholder="Share where you are, and where your heart wants to go…" className="mt-2 bg-transparent border-0 border-b border-brand-forest/25 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-gold text-brand-forest text-lg resize-none" />
                    </div>
                    <button type="submit" data-testid="discovery-submit" disabled={loading} className="group inline-flex items-center gap-3 bg-brand-forest text-brand-cream px-10 py-4 rounded-full text-sm tracking-wide hover:-translate-y-1 hover:shadow-2xl transition-transform duration-300 disabled:opacity-60">
                      {loading ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : (<>Book My Complimentary Discovery Call <Send size={16} className="group-hover:translate-x-1 transition-transform duration-300" /></>)}
                    </button>
                  </form>
                )}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 8 — MEET YOUR ARCHITECT */}
      <section id="architect" className="bg-brand-cream py-28 md:py-40 scroll-mt-24" data-testid="architect-home">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-14 items-center">
          <div className="md:col-span-5">
            <FadeUp>
              <div className="relative arch overflow-hidden aspect-[4/5] max-w-md">
                <img src={IMAGES.architect} alt="Noelle Alicia Bronson, your Holistic Lifestyle Architect" className="w-full h-full object-cover" />
              </div>
            </FadeUp>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <FadeUp><span className="overline">Meet your architect</span></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl text-brand-forest tracking-tight mt-5 leading-[1.05]">
                Because a brighter tomorrow isn't something we wait for — it's something we intentionally create.
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="text-brand-inkSoft text-lg leading-relaxed mt-8 max-w-xl">
                Having walked through seasons of uncertainty, carried the weight of responsibility,
                and learned what it means to rebuild while still showing up for everyone else, Noelle
                understands how easily a woman can lose herself in caring for others. As a nurse,
                birth doula, and Holistic Lifestyle Architect, she helps women stop surviving and
                start intentionally architecting lives that reflect who they truly are.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <div className="mt-8">
                <span className="font-serif italic text-3xl text-brand-forest">{BRAND.architect}</span>
                <div className="mt-3 space-y-0.5 text-brand-inkSoft text-sm leading-relaxed">
                  <p>Holistic Lifestyle Architect</p>
                  <p>LVN, ADN | Home Health &amp; Hospice</p>
                  <p>Certified Postpartum, Labor &amp; Birth Doula</p>
                  <p>HCI Certified Health Coach (HCI-CHC) · HCI Certified Life Coach (HCI-CLC)</p>
                </div>
              </div>
            </FadeUp>
            <FadeUp delay={0.35}>
              <Link
                to="/about"
                data-testid="architect-home-cta"
                className="group inline-flex items-center gap-2 mt-9 font-serif italic text-2xl text-brand-forest link-underline hover:text-brand-gold transition-colors duration-300"
              >
                Meet Noelle <ArrowRight size={22} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* 9 — FAQ */}
      <section className="bg-brand-forest text-brand-cream py-28 md:py-40" data-testid="home-faq">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <FadeUp><span className="overline text-brand-goldBright">Your questions, answered</span></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.02]">
              Before you begin.
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Accordion type="single" collapsible className="mt-12" data-testid="home-faq-accordion">
              {HOME_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-brand-forestSoft/40" data-testid={`home-faq-item-${i}`}>
                  <AccordionTrigger className="font-serif text-xl md:text-2xl text-brand-cream text-left hover:no-underline hover:text-brand-goldBright py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-brand-cream/75 text-base leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>

      {/* 10 — FINAL CTA */}
      <section className="bg-brand-beige py-32 md:py-48" data-testid="home-final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeUp>
            <SunriseCircle className="text-brand-gold mx-auto mb-8" size={44} strokeWidth={1.2} />
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-brand-forest tracking-tight leading-[1.03]">
              The life you're imagining doesn't begin someday. It begins with choosing yourself.
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="mt-12 flex flex-col sm:flex-row justify-center flex-wrap gap-4">
              <button
                data-testid="final-cta-primary"
                onClick={() => scrollTo("#discovery-call")}
                className="group inline-flex items-center justify-center gap-3 bg-brand-forest text-brand-cream px-10 py-4 rounded-full text-sm tracking-wide font-medium hover:-translate-y-1 hover:shadow-2xl transition-transform duration-300"
              >
                Begin My Brighter Tomorrow
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <button
                data-testid="final-cta-secondary"
                onClick={() => scrollTo("#discovery-call")}
                className="inline-flex items-center justify-center gap-3 border border-brand-forest/30 text-brand-forest px-10 py-4 rounded-full text-sm tracking-wide hover:bg-brand-forest/5 transition-colors duration-300"
              >
                Book a Discovery Call
              </button>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-brand-cream/95 backdrop-blur-lg border-t border-brand-gold/20 p-3 pb-5" data-testid="sticky-mobile-cta-wrap">
        <button
          data-testid="sticky-mobile-cta"
          onClick={() => scrollTo("#discovery-call")}
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-forest text-brand-cream font-medium text-base py-3.5 rounded-full"
        >
          Ready to actually start living? Tap here
          <ArrowRight size={18} />
        </button>
      </div>

      {/* Waitlist dialog — The Gathering priority list */}
      <Dialog open={wlOpen} onOpenChange={setWlOpen}>
        <DialogContent className="bg-brand-cream border-brand-beige sm:max-w-md" data-testid="priority-dialog">
          {wlDone ? (
            <div className="text-center py-6" data-testid="priority-success">
              <SunriseCircle className="text-brand-gold mx-auto" size={40} />
              <h3 className="font-serif text-3xl text-brand-forest mt-5">You're on the list.</h3>
              <p className="text-brand-inkSoft mt-3 leading-relaxed">
                Thank you for your interest in The Gathering. We'll reach out personally when the next
                cohort opens — dates are set based on enrollment.
              </p>
              <button data-testid="priority-close" onClick={() => setWlOpen(false)} className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm tracking-wide bg-brand-forest text-brand-cream hover:-translate-y-0.5 transition-transform duration-300">
                Close
              </button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-brand-gold">
                  <Sparkles size={16} />
                  <span className="overline">The Gathering · Priority List</span>
                </div>
                <DialogTitle className="font-serif text-3xl text-brand-forest tracking-tight text-left mt-1">
                  Join the priority list
                </DialogTitle>
                <DialogDescription className="text-brand-inkSoft text-left leading-relaxed">
                  Add your details and we'll invite you first when the next Gathering opens — dates are
                  set based on enrollment.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submitWaitlist} className="space-y-4 mt-2" data-testid="priority-form">
                <div>
                  <Label htmlFor="p-name" className="text-brand-forest text-sm">Full name</Label>
                  <Input id="p-name" data-testid="priority-name" value={wlForm.name} onChange={(e) => setWlForm({ ...wlForm, name: e.target.value })} placeholder="Your name" className="mt-1.5 bg-white border-brand-beige" />
                </div>
                <div>
                  <Label htmlFor="p-email" className="text-brand-forest text-sm">Email</Label>
                  <Input id="p-email" type="email" data-testid="priority-email" value={wlForm.email} onChange={(e) => setWlForm({ ...wlForm, email: e.target.value })} placeholder="you@email.com" className="mt-1.5 bg-white border-brand-beige" />
                </div>
                <div>
                  <Label htmlFor="p-phone" className="text-brand-forest text-sm">Phone <span className="text-brand-inkSoft/60">(optional)</span></Label>
                  <Input id="p-phone" data-testid="priority-phone" value={wlForm.phone} onChange={(e) => setWlForm({ ...wlForm, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1.5 bg-white border-brand-beige" />
                </div>
                <button type="submit" data-testid="priority-submit" disabled={wlLoading} className="group w-full inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-sm tracking-wide bg-brand-forest text-brand-cream hover:-translate-y-0.5 hover:shadow-xl transition-transform duration-300 disabled:opacity-60">
                  {wlLoading ? (<><Loader2 size={18} className="animate-spin" /> Adding you…</>) : (<>Join the priority list <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" /></>)}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
